import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const StreakContext = createContext(null);
export const useStreak = () =>
  useContext(StreakContext) || { streak: null, board: null, refresh: () => {} };

const RANK_KEY = "chatmilu.streak.rank";

const cheer = (text) =>
  window.dispatchEvent(new CustomEvent("chatmilu:reward", { detail: text }));

export function StreakProvider({ children }) {
  const { user, refresh: refreshUser } = useAuth();
  const [streak, setStreak] = useState(null);
  const [board, setBoard] = useState(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setStreak(null);
      setBoard(null);
      return;
    }
    try {
      const [{ data }, lb] = await Promise.all([
        api.get("/streak"),
        api.get("/streak/leaderboard").catch(() => null),
      ]);
      setStreak(data);
      if (lb) setBoard(lb.data);

      // Invariant: /api/streak only reports new_awards once per milestone per streak cycle,
      // so refreshUser() here cannot loop back into another award.
      const award = data.new_awards?.[data.new_awards.length - 1];
      if (award) {
        toast.success(`${data.current}-day streak — ${award.label} unlocked!`);
        refreshUser?.();
        cheer(
          `${data.current} days in a row — that is a real streak. I just unlocked ${award.label} ` +
            "on your account. Come back tomorrow and keep it alive!",
        );
      }

      const rank = lb?.data?.me?.rank;
      if (rank) {
        const before = Number(localStorage.getItem(RANK_KEY)) || 0;
        localStorage.setItem(RANK_KEY, String(rank));
        if (!award && before && rank < before) {
          cheer(
            rank <= 3
              ? `You just moved up to #${rank} on the streak board — top three! Keep that fire going.`
              : `You overtook someone — you are #${rank} on the streak board now. Onwards!`,
          );
        }
      }
    } catch (e) {
      console.error("streak load failed", e);
    }
  }, [user, refreshUser]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onSent = () => refresh();
    window.addEventListener("chatmilu:chatsent", onSent);
    return () => window.removeEventListener("chatmilu:chatsent", onSent);
  }, [refresh]);

  const value = useMemo(() => ({ streak, board, refresh }), [streak, board, refresh]);

  return <StreakContext.Provider value={value}>{children}</StreakContext.Provider>;
}
